import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Button, Space, Popconfirm, message, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import RoleModal from './RoleModal';
import Can from '../../Component/Can';
import NameByLang from '../../Component/nameByLang';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const Roles = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Delete role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: number) => Api.delete(`/api/admin/roles/${roleId}`),
    onSuccess: () => {
      message.success(t('roles.deleteSuccess', 'Role deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/roles'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.msg || t('common.error', 'Error deleting role');
      message.error(errorMessage);
    },
  });

  const handleDelete = (roleId: number) => {
    deleteRoleMutation.mutate(roleId);
  };

  const handleEdit = (record: any) => {
    setSelectedRole(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedRole(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedRole(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/roles'] });
  };

  return (
    <AnimatedContainer className="roles-page" variant="fadeUp">
      <Table
        tableQueryKey="/api/admin/roles"
        tableTitle={t('roles.title', 'Roles Management')}
        showSearch={true}
        columns={[
          {
            title: t('roles.columns.displayName', 'Display Name'),
            dataIndex: 'display_name',
            key: 'display_name',
            render: (_: string, record: any) => (
              <NameByLang name_en={record.display_name_en} name_ar={record.display_name_ar} />
            ),
          },
          {
            title: t('roles.columns.description', 'Description'),
            dataIndex: 'description',
            key: 'description',
            render: (_: string, record: any) => (
              <NameByLang name_en={record.description_en} name_ar={record.description_ar} />
            ),
          },
          {
            title: t('roles.columns.permissions', 'Permissions'),
            key: 'permissions',
            render: (_: any, record: any) => {
              // Get unique permissions (in case of duplicates)
              // Show all permissions assigned to the role (both manual and from models)
              const uniquePermissions = record.permissions
                ? Array.from(
                    new Map(record.permissions.map((perm: any) => [perm.id, perm])).values()
                  )
                : [];
              const permissionsCount = uniquePermissions.length;

              return (
                <Space wrap>
                  {uniquePermissions.slice(0, 3).map((permission: any) => (
                    <Tag key={permission.id} color="green">
                      <NameByLang
                        name_en={permission.display_name_en}
                        name_ar={permission.display_name_ar}
                        name={permission.display_name || permission.name}
                      />
                    </Tag>
                  ))}
                  {permissionsCount > 3 && (
                    <Tag color="default">
                      +{permissionsCount - 3} {t('common.more', 'more')}
                    </Tag>
                  )}
                </Space>
              );
            },
          },
          {
            title: t('common.actions', 'Actions'),
            key: 'actions',
            render: (_: any, record: any) => (
              <Space>
                <Can permission="roles.update">
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    size="small"
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                </Can>
                <Can permission="roles.delete">
                  <Popconfirm
                    title={t('roles.deleteConfirm', 'Are you sure you want to delete this role?')}
                    description={t('roles.deleteWarning', 'This action cannot be undone.')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Popconfirm>
                </Can>
              </Space>
            ),
          },
        ]}
        buttonContent={t('roles.addNew', 'Add New Role')}
        onClickNewButton={handleAddNew}
        createPermission="roles.create"
      />

      <RoleModal
        visible={modalVisible}
        mode={modalMode}
        role={selectedRole}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default Roles;
